/**
 * 2D 仿射矩阵 —— 与 SVG 的 `matrix(a b c d e f)` 同序。
 * 纯数学、零依赖、不碰 DOM：可在 node 里直接执行（见 matrix.test.ts）。
 * 铁律：任何会产出 NaN/Infinity 的运算都必须被拦下（invert 返回 null、toString 兜 0），
 *       否则一次脏变换就会把整棵 SVG 的几何写成 NaN（dongTai 事故的根因）。
 */
export type Mat = readonly [number, number, number, number, number, number];

export interface Pt {
  x: number;
  y: number;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const IDENTITY: Mat = [1, 0, 0, 1, 0, 0];

const EPS = 1e-12;

export const det = (m: Mat): number => m[0] * m[3] - m[1] * m[2];

/** 矩阵乘法 … m ∘ n（先施加 n，再施加 m） */
export const mul = (m: Mat, n: Mat): Mat => [
  m[0] * n[0] + m[2] * n[1],
  m[1] * n[0] + m[3] * n[1],
  m[0] * n[2] + m[2] * n[3],
  m[1] * n[2] + m[3] * n[3],
  m[0] * n[4] + m[2] * n[5] + m[4],
  m[1] * n[4] + m[3] * n[5] + m[5],
];

/** 奇异矩阵（含 NaN）一律返回 null —— 调用方必须显式处理，不许把 NaN 传下去 */
export function invert(m: Mat): Mat | null {
  const d = det(m);
  if (!Number.isFinite(d) || Math.abs(d) < EPS) return null;
  return [
    m[3] / d,
    -m[1] / d,
    -m[2] / d,
    m[0] / d,
    (m[2] * m[5] - m[3] * m[4]) / d,
    (m[1] * m[4] - m[0] * m[5]) / d,
  ];
}

export const apply = (m: Mat, p: Pt): Pt => ({
  x: m[0] * p.x + m[2] * p.y + m[4],
  y: m[1] * p.x + m[3] * p.y + m[5],
});

export const translate = (x: number, y: number): Mat => [1, 0, 0, 1, x, y];

export const scale = (sx: number, sy: number = sx): Mat => [sx, 0, 0, sy, 0, 0];

export function rotate(deg: number): Mat {
  const r = (deg * Math.PI) / 180;
  const c = Math.cos(r);
  const s = Math.sin(r);
  return [c, s, -s, c, 0, 0];
}

/** 在 (cx, cy) 处施加 m：T(c) · m · T(-c) */
export const around = (m: Mat, cx: number, cy: number): Mat =>
  mul(mul(translate(cx, cy), m), translate(-cx, -cy));

/** Shift 等比约束：取绝对值较大的轴，保留各自方向 */
export function uniform(sx: number, sy: number): { sx: number; sy: number } {
  const k = Math.max(Math.abs(sx), Math.abs(sy));
  return { sx: Math.sign(sx) * k, sy: Math.sign(sy) * k };
}

export const fromCTM = (m: DOMMatrix): Mat => [m.a, m.b, m.c, m.d, m.e, m.f];

export const isFiniteMat = (m: Mat): boolean => m.every((v) => Number.isFinite(v));

export const isIdentityMat = (m: Mat, eps = 1e-9): boolean =>
  m.every((v, i) => Math.abs(v - IDENTITY[i]) < eps);

/** 序列化为 SVG transform 值；非有限数一律归 0（最后一道防线） */
export const toString = (m: Mat): string =>
  `matrix(${m.map((v) => (Number.isFinite(v) ? +v.toFixed(6) : 0)).join(' ')})`;
